import {
  CustomErrorToast,
  CustomSuccessToast,
} from '@/@straico-ui/commons/CustomToasts';
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  IconButton,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery as useMediaQueryMUI,
} from '@mui/material';
import { format } from 'date-fns';
import { get } from 'lodash';
import { inject, observer } from 'mobx-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { cn } from 'utils/styles';
// Store
import useChatStore, { FORK_MEDIA, GO_TO_MEDIA } from 'Components/Chat/store';
import useThemeStore from 'Theme/store';
// Icons
import ModelsIcon from '@/Theme/icons/modelsIcon';
import { icons as modelIcons } from '@/Theme/icons/modelsIcons/index';
import {
  ArrowDownTrayIcon,
  BoltIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon,
  PaintBrushIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import FemaleIcon from '@mui/icons-material/Female';
import MaleIcon from '@mui/icons-material/Male';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
// Components
import AudioPlayer from './AudioPlayer';

// Constants for modal layout
const MODAL_MARGIN = 32; // 32px margin on each side
const MODAL_PADDING = 24; // 24px padding on each side
const MODAL_SPACING = (MODAL_MARGIN + MODAL_PADDING) * 2; // Total space on both sides

const MediaDetailModal = inject('store')(
  observer(({ store, open, onClose, mediaItem, onDeleteMedia }) => {
    const addEvent = useChatStore((state) => state.addEvent);
    const navigate = useNavigate();
    const { theme } = useThemeStore();

    const [modelDetails, setModelDetails] = useState(null);
    const [loadingModelDetails, setLoadingModelDetails] = useState(true);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isPublic, setIsPublic] = useState(mediaItem?.public || false);
    const [updatingVisibility, setUpdatingVisibility] = useState(false);

    const isSmallMobile = useMediaQueryMUI((theme) =>
      theme?.breakpoints?.down('sm')
    );

    useEffect(() => {
      const handleResize = () => {
        setWindowHeight(window.innerHeight);
        setWindowWidth(window.innerWidth);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
      if (mediaItem) {
        setIsPublic(mediaItem.public || false);
      }
    }, [mediaItem]);

    if (!mediaItem) return null;

    const calculateLandscapeDimensions = () => {
      if (!mediaItem.height || !mediaItem.width) {
        return {
          height: '100%',
          width: 'auto',
        };
      }

      const maxHeight = windowHeight * 0.5; // 50% of window height
      const maxAvailableWidth = window.innerWidth - MODAL_SPACING;

      // Calculate dimensions based on maximum height
      const heightScale = Math.min(maxHeight / mediaItem.height, 1);
      const widthFromHeight = mediaItem.width * heightScale;

      // Calculate dimensions based on maximum width
      const widthScale = Math.min(maxAvailableWidth / mediaItem.width, 1);
      const heightFromWidth = mediaItem.height * widthScale;

      // Use dimensions that result in the smallest size
      if (widthFromHeight <= maxAvailableWidth) {
        return {
          height: `${Math.min(
            mediaItem.height * heightScale,
            mediaItem.height
          )}px`,
          width: `${widthFromHeight}px`,
        };
      } else {
        return {
          height: `${Math.min(
            mediaItem.height * widthScale,
            mediaItem.height
          )}px`,
          width: `${Math.min(mediaItem.width * widthScale, mediaItem.width)}px`,
        };
      }
    };

    const calculatePortraitDimensions = () => {
      if (!mediaItem.height || !mediaItem.width) {
        return {
          height: 'auto',
          width: 'auto',
        };
      }

      const maxWidth = window.innerWidth * 0.5; // 50% of window width
      const maxAvailableHeight = windowHeight - MODAL_SPACING;

      // Calculate dimensions based on maximum width
      const widthScale = Math.min(maxWidth / mediaItem.width, 1);
      const heightFromWidth = mediaItem.height * widthScale;

      // Calculate dimensions based on maximum height
      const heightScale = Math.min(maxAvailableHeight / mediaItem.height, 1);
      const widthFromHeight = mediaItem.width * heightScale;

      // Use dimensions that result in the smallest size
      if (heightFromWidth <= maxAvailableHeight) {
        return {
          height: `${Math.min(
            mediaItem.height * widthScale,
            mediaItem.height
          )}px`,
          width: `${Math.min(mediaItem.width * widthScale, mediaItem.width)}px`,
        };
      } else {
        return {
          height: `${Math.min(
            mediaItem.height * heightScale,
            mediaItem.height
          )}px`,
          width: `${widthFromHeight}px`,
        };
      }
    };

    const getIcon = (model) => {
      const Icon = get(modelIcons, model.icon);
      return Icon ? (
        <Icon
          className="h-[18px] w-[18px]"
          fillColor={model.color || '#464BBA'}
        />
      ) : (
        <ModelsIcon className="h-[18px] w-[18px] stroke-white" />
      );
    };

    const getImageProfile = () => {
      return (
        <>
          {mediaItem.creator?.avatar?.svg ? (
            <div className="avatar">
              <div className="w-8 rounded">
                <div
                  dangerouslySetInnerHTML={{
                    __html: mediaItem.creator.avatar.svg,
                  }}
                />
              </div>
            </div>
          ) : (
            <UserCircleIcon className="size-8 transition text-cool-gray dark:text-battleship-gray" />
          )}
        </>
      );
    };

    const handleDownloadClick = async () => {
      const url = mediaItem.url;
      const fileName = url.substring(url.lastIndexOf('/') + 1) || 'image.png';

      const link = document.createElement('a');
      link.href = url;
      link.style.display = 'none';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const handleGoToChatClick = () => {
      addEvent(GO_TO_MEDIA, [mediaItem.chat, mediaItem.message]);
      navigate('/chat');
    };

    const handleForkGeneration = () => {
      const { prompt, image_url, ...params } = mediaItem.generation_params;
      addEvent(FORK_MEDIA, [
        prompt,
        { ...params, attachment: image_url ? 1 : undefined },
        modelDetails,
        image_url,
      ]);
      navigate('/chat');
    };

    const isDeveloper = () => {
      return (
        store.profile.permissions &&
        store.profile.permissions.includes('developer')
      );
    };

    const handleVisibilityChange = async (event) => {
      const newPublicState = event.target.checked;
      setUpdatingVisibility(true);

      try {
        const response = await store.api.put(
          `/v2/ai/media/${mediaItem._id}/visibility`,
          {
            isPublic: newPublicState,
          }
        );

        if (response.data.status) {
          setIsPublic(newPublicState);
          toast.custom(
            (t) => (
              <CustomSuccessToast
                message={`Media ${
                  newPublicState ? 'made public' : 'made private'
                } successfully`}
              />
            ),
            { duration: 2000 }
          );
        } else {
          throw new Error(response.data.message || 'Error updating visibility');
        }
      } catch (error) {
        console.error('Error updating media visibility:', error);
        toast.custom(
          (t) => (
            <CustomErrorToast
              message={
                error.response?.data?.message ||
                'Error updating media visibility'
              }
            />
          ),
          { duration: 2000 }
        );
      } finally {
        setUpdatingVisibility(false);
      }
    };

    const fetchModelDetails = async () => {
      if (!mediaItem || !mediaItem.model || !mediaItem.type) return;

      setLoadingModelDetails(true);
      try {
        const response = await store.api.get(
          `/v2/ai/model/${mediaItem.model._id}`,
          {
            params: { type: mediaItem.type },
          }
        );
        if (response.data.status) {
          setModelDetails(response.data.data);
        } else {
          console.error('Error fetching model details:', response.data.message);
          setModelDetails(null);
        }
      } catch (error) {
        console.error('Error fetching model details:', error);
        setModelDetails(null);
      } finally {
        setLoadingModelDetails(false);
      }
    };

    const renderParameters = () => {
      if (loadingModelDetails) {
        return (
          <div className="text-sm md:text-base lg:text-lg xl:text-xl">
            Loading parameters...
          </div>
        );
      }
      if (!modelDetails || !modelDetails.parameters) {
        return (
          <div className="text-sm md:text-base lg:text-lg xl:text-xl">
            Could not load parameters.
          </div>
        );
      }

      const paramsToDisplay = Object.keys(mediaItem.generation_params).filter(
        (key) => key !== 'prompt' && key !== 'image_url'
      );

      if (paramsToDisplay.length === 0) {
        return (
          <div className="text-sm md:text-base lg:text-lg xl:text-xl">
            No additional parameters.
          </div>
        );
      }

      return (
        <div
          className={cn('grid gap-2 md:gap-3 lg:gap-4 xl:gap-5 w-full', {
            'grid-cols-1': isSmallMobile,
            'grid-cols-2': !isSmallMobile,
          })}
        >
          {paramsToDisplay.map((key) => {
            const parameterDetail = modelDetails.parameters.find(
              (param) => param.property === key
            );

            if (!parameterDetail) {
              return null; // Don't display if parameter details are not found
            }

            let displayValue = mediaItem.generation_params[key];

            // Handle dropdown type to show label instead of value
            if (
              parameterDetail.type === 'dropdown' &&
              parameterDetail.options
            ) {
              const selectedOption = parameterDetail.options.find(
                (option) => option.value === displayValue
              );
              if (selectedOption) {
                displayValue = selectedOption.label;
              } else {
                // If value not found in options, just display the value
                displayValue = mediaItem.generation_params[key];
              }
            }

            // Format boolean values
            if (parameterDetail.type === 'boolean') {
              displayValue = displayValue ? 'True' : 'False';
            }
            // Format number values - basic formatting
            if (parameterDetail.type === 'number') {
              displayValue = Number(displayValue).toLocaleString(); // Basic number formatting
            }

            if (parameterDetail.type === 'action') {
              switch (parameterDetail.property) {
                case 'attachment':
                  displayValue = {
                    type: 'link',
                    url: mediaItem.generation_params[key].image_url.url,
                    text: 'Click here',
                  };
                  break;
                default:
                  return null;
              }
            }

            return (
              <div key={key} className="flex flex-col gap-3">
                <div className="font-bold text-sm md:text-lg lg:text-xl xl:text-[22px] xl:leading-7">
                  {parameterDetail.label}
                </div>
                <div>
                  {displayValue !== null && displayValue !== undefined ? (
                    typeof displayValue === 'object' &&
                    displayValue.type === 'link' ? (
                      <a
                        className="text-nue-blue underline"
                        href={displayValue.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {displayValue.text}
                      </a>
                    ) : (
                      String(displayValue)
                    )
                  ) : (
                    'N/A'
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    };

    const getButtons = () => {
      return (
        <>
          {store.profile._id != mediaItem.creator._id ? (
            <div></div>
          ) : (
            <Button
              variant="filled"
              color="inherit"
              onClick={handleGoToChatClick}
              startIcon={<ChatBubbleLeftRightIcon className="h-4 w-4" />}
              sx={{
                backgroundColor: theme == 'dark' ? '#3B3B45' : '#D4D5E6',
                color: theme == 'dark' ? '#7F82C3' : '#5256A6',
                '&:hover': {
                  backgroundColor: theme == 'dark' ? '#3B3B45' : '#D4D5E6',
                  opacity: 0.8,
                },
              }}
            >
              Go to chat
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownloadClick}
            startIcon={<ArrowDownTrayIcon className="h-4 w-4" />}
          >
            Download
          </Button>
        </>
      );
    };

    const getGenderIcon = () => {
      switch (mediaItem.metadata?.gender) {
        case 'male':
          return <MaleIcon sx={{ color: '#51526C', fontSize: 18 }} />;
        case 'female':
          return <FemaleIcon sx={{ color: '#51526C', fontSize: 18 }} />;
        default:
          return <QuestionMarkIcon sx={{ color: '#51526C', fontSize: 18 }} />;
      }
    };

    const getGenerationInfo = () => {
      return (
        <>
          {/* Model and date */}
          <div
            className={cn(
              'grid gap-2 md:gap-3 lg:gap-4 xl:gap-5 mb-2 md:mb-3 lg:mb-4 xl:mb-5 w-full',
              {
                'grid-cols-1': isSmallMobile,
                'grid-cols-2': !isSmallMobile,
              }
            )}
          >
            <div
              className={cn('flex gap-3', {
                'flex-col': !isSmallMobile,
                'justify-between': isSmallMobile,
              })}
            >
              <div className="font-bold text-sm md:text-lg lg:text-xl xl:text-[22px] xl:leading-7">
                Model
              </div>
              <div
                className={`flex w-fit items-center gap-1 font-figtree font-bold text-sm text-white uppercase px-[6px] py-[3px] bg-${
                  mediaItem.model.color
                    ? `[${mediaItem.model.color}]`
                    : 'nue-blue'
                } rounded-lg`}
              >
                {getIcon(mediaItem.model)}
                <div className="line-clamp-1">
                  {mediaItem.model.abbreviation}
                </div>
              </div>
            </div>
            {store.profile._id != mediaItem.creator._id && (
              <div className="flex flex-col gap-3">
                <div className="font-bold text-sm md:text-lg lg:text-xl xl:text-[22px] xl:leading-7 line-clamp-1">
                  Creator
                </div>
                <div className="flex items-center gap-2">
                  {getImageProfile()}
                  {mediaItem.creator.fname} {mediaItem.creator.lname}
                </div>
              </div>
            )}
            <div className="flex flex-col gap-3">
              <div className="font-bold text-sm md:text-lg lg:text-xl xl:text-[22px] xl:leading-7 line-clamp-1">
                Date of Creation
              </div>
              <div>
                {mediaItem.created
                  ? format(new Date(mediaItem.created), 'MMMM dd, yyyy')
                  : 'N/A'}
              </div>
            </div>
            {mediaItem.optimized && (
              <div className="flex w-fit items-center gap-1 font-figtree font-bold text-sm px-[6px] py-[3px] bg-peachy-maroney text-white rounded-lg">
                <BoltIcon className="h-[14px] w-[14px]" />
                <span className="text-xs">OPTIMIZED</span>
              </div>
            )}
          </div>

          {/* Params */}
          {renderParameters()}
        </>
      );
    };

    const handleCopyPrompt = () => {
      const promptText = mediaItem.generation_params?.prompt;
      if (promptText) {
        navigator.clipboard
          .writeText(promptText)
          .then(() => {
            toast.custom(
              (t) => (
                <CustomSuccessToast message="Prompt copied to clipboard" />
              ),
              { duration: 1500 }
            );
          })
          .catch((err) => {
            console.error('Error copying prompt:', err);
            toast.custom(
              (t) => <CustomErrorToast message="Error copying prompt" />,
              { duration: 1500 }
            );
          });
      }
    };

    const getPrompt = () => {
      return (
        <div className="flex flex-col gap-3 relative">
          <div className="font-bold text-sm md:text-lg lg:text-xl xl:text-[22px] xl:leading-7">
            Prompt
          </div>
          <div className="flex gap-2 absolute right-0 top-0">
            <Tooltip
              title="Remix Generation"
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme == 'dark' ? '#3B3B45' : '#D4D5E6',
                    color: theme == 'dark' ? '#EFEFEF' : '#5256A6',
                    '& .MuiTooltip-arrow': {
                      color: theme == 'dark' ? '#3B3B45' : '#D4D5E6',
                    },
                  },
                },
              }}
            >
              <IconButton
                size="small"
                onClick={handleForkGeneration}
                sx={{
                  color: theme === 'dark' ? '#7F82C3' : '#5256A6',
                }}
              >
                <PaintBrushIcon className="h-4 w-4" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title="Copy prompt"
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme == 'dark' ? '#3B3B45' : '#D4D5E6',
                    color: theme == 'dark' ? '#EFEFEF' : '#5256A6',
                    '& .MuiTooltip-arrow': {
                      color: theme == 'dark' ? '#3B3B45' : '#D4D5E6',
                    },
                  },
                },
              }}
            >
              <IconButton
                size="small"
                onClick={handleCopyPrompt}
                sx={{
                  color: theme === 'dark' ? '#7F82C3' : '#5256A6',
                }}
              >
                <DocumentDuplicateIcon className="h-4 w-4" />
              </IconButton>
            </Tooltip>
          </div>
          <div>{mediaItem.generation_params?.prompt}</div>
        </div>
      );
    };

    const functions = {
      fetchModelDetails,
      getGenerationInfo,
      getButtons,
      getPrompt,
      calculateLandscapeDimensions,
      calculatePortraitDimensions,
      handleDeleteMedia: onDeleteMedia,
    };

    const renderDetailContent = () => {
      switch (mediaItem.type) {
        case 'image':
          return (
            <ImageDetail
              store={store}
              mediaItem={mediaItem}
              functions={functions}
            />
          );
        case 'audio':
          return (
            <AudioDetail
              store={store}
              mediaItem={mediaItem}
              functions={functions}
            />
          );
        case 'video':
          return (
            <VideoDetail
              store={store}
              mediaItem={mediaItem}
              functions={functions}
            />
          );
        default:
          return <Typography>Unsupported media type</Typography>;
      }
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: {
              xs: '16px',
              sm: '18px',
              md: '19px',
              lg: '20px',
              xl: '21px',
            },
            position: 'relative',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: 'max-content',
            overflow: 'auto',
          },
        }}
        sx={{
          '& .MuiPaper-root': {
            background: theme === 'dark' ? '#252425' : '#F7F7F8',
          },
        }}
      >
        <IconButton
          size="small"
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            zIndex: 1,
          }}
        >
          <XMarkIcon
            className={cn('h-3 w-3', {
              'text-cool-gray': theme === 'light',
              'text-palladium': theme === 'dark',
            })}
          />
        </IconButton>

        {/* Public Visibility Control */}
        {isDeveloper() && store.profile._id === mediaItem.creator._id && (
          <Tooltip
            title={
              <div className="text-center p-1">
                <div className="font-semibold text-sm">
                  {isPublic ? 'Make Private' : 'Make Public'}
                </div>
                <div className="text-xs mt-1">
                  {isPublic ? 'Hide from all users' : 'Show to all users'}
                </div>
              </div>
            }
            arrow
            placement="right"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  color: theme === 'dark' ? '#EFEFEF' : '#5256A6',
                  '& .MuiTooltip-arrow': {
                    color: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  },
                },
              },
            }}
          >
            <div
              className={cn(
                'absolute flex items-center left-2 top-2 gap-2 p-2 rounded-lg shadow-lg border transition-all duration-200 hover:scale-105',
                {
                  'bg-seasalt border-cool-gray/30': theme === 'light',
                  'bg-lead border-tornado-cloud': theme === 'dark',
                }
              )}
              style={{
                zIndex: 10,
              }}
            >
              <div
                className={cn('text-xs font-medium', {
                  'text-cool-gray': theme === 'light',
                  'text-crystal-bell': theme === 'dark',
                })}
              >
                {isPublic ? '🌍' : '🔒'}
              </div>
              <Switch
                checked={isPublic}
                onChange={handleVisibilityChange}
                disabled={updatingVisibility}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#5256A6',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#5256A6',
                  },
                  '& .MuiSwitch-track': {
                    backgroundColor: theme === 'dark' ? '#505050' : '#D0D0D8',
                  },
                }}
              />
            </div>
          </Tooltip>
        )}

        <div className="flex flex-1 flex-col w-full h-full overflow-auto overflow-x-hidden">
          {renderDetailContent()}
        </div>
      </Dialog>
    );
  })
);

// Componente de detalle para imágenes
const ImageDetail = ({ store, mediaItem, functions }) => {
  const {
    fetchModelDetails,
    getGenerationInfo,
    getButtons,
    getPrompt,
    calculateLandscapeDimensions,
    calculatePortraitDimensions,
    handleDeleteMedia,
  } = functions;
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(
    mediaItem.thumbnails?.large ||
      mediaItem.thumbnails?.medium ||
      mediaItem.thumbnails?.small ||
      mediaItem.url
  );
  const imageRef = useRef(null);
  const { theme } = useThemeStore();

  const isSmallMobile = useMediaQueryMUI((theme) =>
    theme?.breakpoints?.down('sm')
  );

  const aspectRatio = useMemo(() => {
    if (mediaItem.width && mediaItem.height) {
      return mediaItem.width / mediaItem.height;
    }
    return 0;
  }, [mediaItem.width, mediaItem.height]);

  const isLandscape = useMemo(() => aspectRatio > 1, [aspectRatio]);

  const calculateDimensions = useMemo(() => {
    return isLandscape || isSmallMobile
      ? calculateLandscapeDimensions()
      : calculatePortraitDimensions();
  }, [
    calculateLandscapeDimensions,
    calculatePortraitDimensions,
    isLandscape,
    isSmallMobile,
  ]);

  useEffect(() => {
    const highResImage = new Image();
    highResImage.src = mediaItem.url;
    highResImage.onload = () => {
      setCurrentSrc(mediaItem.url);
      setIsLoaded(true);
    };
  }, [mediaItem.url]);

  useEffect(() => {
    fetchModelDetails();
  }, []);

  return (
    <div
      className={cn(
        `flex-1 w-${
          isLandscape ? `[${calculateDimensions.width}]` : 'full'
        } h-full flex gap-2 md:gap-3 lg:gap-4 xl:gap-5 overflow-auto`,
        {
          'flex-col': isLandscape || isSmallMobile,
          'flex-row': !isLandscape && !isSmallMobile,
        }
      )}
    >
      {isSmallMobile && (
        <div className="font-bold text-base md:text-lg lg:text-2xl xl:text-[28px] xl:leading-7 w-full text-center">
          About Creation
        </div>
      )}
      <div className="relative">
        <CardMedia
          ref={imageRef}
          component="img"
          image={currentSrc}
          alt={mediaItem.type}
          sx={{
            height: calculateDimensions.height,
            width: calculateDimensions.width,
            objectFit: mediaItem.width && mediaItem.height ? 'contain' : '',
            borderRadius: {
              xs: '16px',
              sm: '18px',
              md: '19px',
              lg: '20px',
              xl: '21px',
            },
            transition: 'filter 0.3s ease-in-out',
            filter: isLoaded ? 'none' : 'blur(10px)',
            margin: isSmallMobile ? 'auto' : '',
          }}
        />
        {handleDeleteMedia && store.profile._id === mediaItem.creator._id && (
          <Tooltip
            title="Delete media"
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  color: theme === 'dark' ? '#EFEFEF' : '#5256A6',
                  '& .MuiTooltip-arrow': {
                    color: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  },
                },
              },
            }}
          >
            <IconButton
              onClick={() => handleDeleteMedia(mediaItem)}
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: '#BC4D4D',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#BC4D4D',
                  opacity: 0.8,
                },
                zIndex: 1,
              }}
            >
              <TrashIcon className="h-5 w-5" />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <div
        className={cn(
          `flex-1 flex flex-col gap-4 font-figtree text-raisin-black dark:text-crystal-bell text-xs md:text-sm lg:text-base lg:leading-6 font-normal ${
            isLandscape ? calculateDimensions.width : ''
          } overflow-auto`,
          {
            'max-w-[600px]': !isLandscape,
          }
        )}
      >
        {/* Title */}
        {!isSmallMobile && (
          <>
            {isLandscape ? (
              <div className="flex justify-between">
                <div className="font-bold text-base md:text-lg lg:text-2xl xl:text-[28px] xl:leading-7 flex-1">
                  About Creation
                </div>
                <div className="flex gap-2">{getButtons()}</div>
              </div>
            ) : (
              <div className="font-bold text-base md:text-lg lg:text-2xl xl:text-[28px] xl:leading-7 w-full text-center">
                About Creation
              </div>
            )}
          </>
        )}

        {isLandscape || isSmallMobile ? (
          <div
            className={cn(
              'grid gap-2 overflow-x-hidden overflow-auto overflow-rtl',
              {
                'overflow-rtl-light': theme === 'light',
                'overflow-rtl-dark': theme === 'dark',
                'grid-cols-1': isSmallMobile,
                'grid-cols-2': !isSmallMobile,
              }
            )}
          >
            {/* Prompt */}
            {!isSmallMobile && getPrompt()}
            <div className="flex flex-col gap-3">{getGenerationInfo()}</div>
            {isSmallMobile && (
              <>
                {getPrompt()}
                <div className="flex w-full justify-between gap-2 mt-2">
                  {getButtons()}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-auto">
            <div className="mb-2 md:mb-3 lg:mb-4 xl:mb-5">
              {getGenerationInfo()}
            </div>
            {/* Prompt */}
            {getPrompt()}
          </div>
        )}
        {!isLandscape && !isSmallMobile && (
          <>
            {/* Spacer */}
            <div className="flex-1"></div>
            {/* Buttons */}
            <div className="flex w-full justify-between gap-2">
              {getButtons()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Componente de detalle para audio
const AudioDetail = ({ store, mediaItem, functions }) => {
  const {
    fetchModelDetails,
    getGenerationInfo,
    getButtons,
    getPrompt,
    handleDeleteMedia,
  } = functions;

  const { theme } = useThemeStore();

  const isSmallMobile = useMediaQueryMUI((theme) =>
    theme?.breakpoints?.down('sm')
  );

  useEffect(() => {
    fetchModelDetails();
  }, []);

  const getDeleteAction = () => {
    if (!handleDeleteMedia || store.profile._id !== mediaItem.creator._id)
      return null;

    return (
      <Tooltip
        title="Delete media"
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              fontSize: '14px',
              fontFamily: 'Figtree',
            },
          },
        }}
      >
        <IconButton onClick={() => handleDeleteMedia(mediaItem)}>
          <TrashIcon className="h-5 w-5 text-agate-violet dark:text-lavender" />
        </IconButton>
      </Tooltip>
    );
  };

  const getActions = () => {
    return [getDeleteAction()].filter(Boolean);
  };

  return (
    <div className="flex-1 flex flex-col gap-4 font-figtree text-raisin-black dark:text-crystal-bell text-xs md:text-sm lg:text-base lg:leading-6 font-normal max-w-[676px] overflow-auto">
      {/* Title */}
      <div className="font-bold text-base md:text-lg lg:text-2xl xl:text-[28px] xl:leading-7 w-full text-center">
        About Creation
      </div>
      {/* Audio player */}
      {isSmallMobile && (
        <AudioPlayer
          audioUrl={mediaItem.url}
          allowDonwload={false}
          actions={getActions()}
        />
      )}
      <div
        className={cn('overflow-auto overflow-rtl', {
          'overflow-rtl-light': theme === 'light',
          'overflow-rtl-dark': theme === 'dark',
        })}
      >
        {/* Info */}
        <div className="mb-2 md:mb-3 lg:mb-4 xl:mb-5">
          {getGenerationInfo()}
        </div>
        {/* Prompt */}
        {getPrompt()}
      </div>
      {/* Spacer */}
      <div className="flex-1"></div>
      {/* Audio player */}
      {!isSmallMobile && (
        <AudioPlayer
          audioUrl={mediaItem.url}
          allowDonwload={false}
          actions={getActions()}
        />
      )}
      {/* Buttons */}
      <div className="flex w-full justify-between gap-2">{getButtons()}</div>
    </div>
  );
};

// Componente de detalle para video
const VideoDetail = ({ store, mediaItem, functions }) => {
  const {
    fetchModelDetails,
    getGenerationInfo,
    getButtons,
    getPrompt,
    calculateLandscapeDimensions,
    calculatePortraitDimensions,
    handleDeleteMedia,
  } = functions;

  const videoRef = useRef(null);
  const imageRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const { theme } = useThemeStore();

  const isSmallMobile = useMediaQueryMUI((theme) =>
    theme?.breakpoints?.down('sm')
  );

  const aspectRatio = useMemo(() => {
    if (mediaItem.width && mediaItem.height) {
      return mediaItem.width / mediaItem.height;
    }
    return 16 / 9;
  }, [mediaItem.width, mediaItem.height]);

  const isLandscape = useMemo(() => aspectRatio > 1, [aspectRatio]);

  const calculateDimensions = useMemo(() => {
    return isLandscape || isSmallMobile
      ? calculateLandscapeDimensions()
      : calculatePortraitDimensions();
  }, [
    calculateLandscapeDimensions,
    calculatePortraitDimensions,
    isLandscape,
    isSmallMobile,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
      setShowPlayOverlay(false);
    };
    const handlePause = () => {
      setIsPlaying(false);
      setShowPlayOverlay(true);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setShowPlayOverlay(true);
    };
    const handleLoadedData = () => {
      setIsVideoLoaded(true);
      setShowPlayOverlay(true);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('loadeddata', handleLoadedData);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, []);

  useEffect(() => {
    fetchModelDetails();
  }, []);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  return (
    <div
      className={cn(
        `flex-1 w-${
          isLandscape ? `[${calculateDimensions.width}]` : 'full'
        } h-full flex gap-2 md:gap-3 lg:gap-4 xl:gap-5 overflow-auto`,
        {
          'flex-col': isLandscape || isSmallMobile,
          'flex-row': !isLandscape && !isSmallMobile,
        }
      )}
    >
      {isSmallMobile && (
        <div className="font-bold text-base md:text-lg lg:text-2xl xl:text-[28px] xl:leading-7 w-full text-center">
          About Creation
        </div>
      )}
      <div className="relative">
        <CardMedia
          ref={imageRef}
          component="img"
          image={
            mediaItem.thumbnails?.large ||
            mediaItem.thumbnails?.medium ||
            mediaItem.thumbnails?.small
          }
          alt={mediaItem.type}
          sx={{
            height: calculateDimensions.height,
            width: calculateDimensions.width,
            objectFit: mediaItem.width && mediaItem.height ? 'contain' : '',
            borderRadius: {
              xs: '16px',
              sm: '18px',
              md: '19px',
              lg: '20px',
              xl: '21px',
            },
            transition: 'filter 0.3s ease-in-out',
            opacity: isVideoLoaded ? 0 : 1,
            filter: isVideoLoaded ? 'none' : 'blur(10px)',
            margin: isSmallMobile ? 'auto' : '',
          }}
        />
        <CardMedia
          component="video"
          ref={videoRef}
          src={mediaItem.url}
          playsInline
          preload="auto"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            height: calculateDimensions.height,
            width: calculateDimensions.width,
            objectFit: mediaItem.width && mediaItem.height ? 'contain' : '',
            borderRadius: {
              xs: '16px',
              sm: '18px',
              md: '19px',
              lg: '20px',
              xl: '21px',
            },
            transition: 'opacity 0.3s ease-in-out',
            opacity: isVideoLoaded ? 1 : 0,
            filter: isVideoLoaded ? 'none' : 'blur(10px)',
            margin: isSmallMobile ? 'auto' : '',
          }}
          onClick={togglePlayPause}
        />
        {showPlayOverlay && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
              cursor: 'pointer',
              transition: 'opacity 0.3s ease-in-out',
              opacity: isVideoLoaded ? 1 : 0,
            }}
            onClick={togglePlayPause}
          >
            {isPlaying ? (
              <PauseIcon className="h-16 w-16 text-cool-gray" />
            ) : (
              <PlayIcon className="h-16 w-16 text-cool-gray" />
            )}
          </Box>
        )}
        {handleDeleteMedia && store.profile._id === mediaItem.creator._id && (
          <Tooltip
            title="Delete media"
            arrow
            placement="top"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  color: theme === 'dark' ? '#EFEFEF' : '#5256A6',
                  '& .MuiTooltip-arrow': {
                    color: theme === 'dark' ? '#3B3B45' : '#D4D5E6',
                  },
                },
              },
            }}
          >
            <IconButton
              onClick={() => handleDeleteMedia(mediaItem)}
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: '#BC4D4D',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: '#BC4D4D',
                  opacity: 0.8,
                },
                zIndex: 1,
              }}
            >
              <TrashIcon className="h-5 w-5" />
            </IconButton>
          </Tooltip>
        )}
      </div>
      <div
        className={cn(
          `flex-1 flex flex-col gap-4 font-figtree text-raisin-black dark:text-crystal-bell text-xs md:text-sm lg:text-base lg:leading-6 font-normal ${
            isLandscape ? calculateDimensions.width : ''
          } overflow-auto`,
          {
            'max-w-[600px]': !isLandscape,
          }
        )}
      >
        {/* Title */}
        {!isSmallMobile && (
          <>
            {isLandscape ? (
              <div className="flex justify-between">
                <div className="font-bold text-base md:text-lg lg:text-2xl xl:text-[28px] xl:leading-7 flex-1">
                  About Creation
                </div>
                <div className="flex gap-2">{getButtons()}</div>
              </div>
            ) : (
              <div className="font-bold text-base md:text-lg lg:text-2xl xl:text-[28px] xl:leading-7 w-full text-center">
                About Creation
              </div>
            )}
          </>
        )}

        {isLandscape || isSmallMobile ? (
          <div
            className={cn(
              'grid gap-2 overflow-x-hidden overflow-auto overflow-rtl',
              {
                'overflow-rtl-light': theme === 'light',
                'overflow-rtl-dark': theme === 'dark',
                'grid-cols-1': isSmallMobile,
                'grid-cols-2': !isSmallMobile,
              }
            )}
          >
            {/* Prompt */}
            {!isSmallMobile && getPrompt()}
            <div className="flex flex-col gap-3">{getGenerationInfo()}</div>
            {isSmallMobile && (
              <>
                {getPrompt()}
                <div className="flex w-full justify-between gap-2 mt-2">
                  {getButtons()}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-auto">
            <div className="mb-2 md:mb-3 lg:mb-4 xl:mb-5">
              {getGenerationInfo()}
            </div>
            {/* Prompt */}
            {getPrompt()}
          </div>
        )}
        {!isLandscape && !isSmallMobile && (
          <>
            {/* Spacer */}
            <div className="flex-1"></div>
            {/* Buttons */}
            <div className="flex w-full justify-between gap-2">
              {getButtons()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MediaDetailModal;
